import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  DollarSign, 
  Link2, 
  TrendingUp, 
  MousePointer,
  ShoppingCart,
  Wallet,
  Copy,
  ExternalLink,
  Clock,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { motion } from "framer-motion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AffiliatePage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [links, setLinks] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [creatingLink, setCreatingLink] = useState(false);
  const [requestingPayout, setRequestingPayout] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, linksRes, payoutsRes] = await Promise.all([
        axios.get(`${API}/affiliate/stats`, { headers }),
        axios.get(`${API}/affiliate/links`, { headers }),
        axios.get(`${API}/affiliate/payouts`, { headers })
      ]);

      setStats(statsRes.data);
      setLinks(linksRes.data.links);
      setPayouts(payoutsRes.data.payouts);
    } catch (error) {
      console.error("Error fetching affiliate data:", error);
    } finally {
      setLoading(false);
    }
  };

  const createAffiliateLink = async (e) => {
    e.preventDefault();
    if (!newLinkUrl.trim()) return;

    setCreatingLink(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/affiliate/create-link`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            content_id: "general",
            marketplace_url: newLinkUrl
          }
        }
      );

      toast.success("Affiliate link created!");
      setNewLinkUrl("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create link");
    } finally {
      setCreatingLink(false);
    }
  };

  const copyLink = async (url) => {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard!");
  };

  const requestPayout = async () => {
    setRequestingPayout(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/affiliate/request-payout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request payout");
    } finally {
      setRequestingPayout(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="max-w-5xl mx-auto animate-pulse space-y-4">
          <div className="h-8 w-48 bg-gray-200" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-6 h-6 text-[#CCFF00]" />
            <span className="text-sm font-mono uppercase tracking-widest text-gray-500">
              AFFILIATE PROGRAM
            </span>
          </div>
          <h1 className="text-4xl font-extrabold tracking-tighter mb-4">
            Earn While You Share
          </h1>
          <p className="text-gray-600 max-w-xl">
            Share products from connected marketplaces and earn {stats?.commission_rate}% commission 
            on every sale. Premium members earn money when their shared content drives sales!
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          <div className="neo-card p-6 text-center">
            <MousePointer className="w-6 h-6 mx-auto mb-2 text-[#7C3AED]" />
            <div className="text-3xl font-bold">{stats?.total_clicks || 0}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Total Clicks</div>
          </div>
          <div className="neo-card p-6 text-center">
            <ShoppingCart className="w-6 h-6 mx-auto mb-2 text-[#FF0099]" />
            <div className="text-3xl font-bold">{stats?.total_conversions || 0}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Conversions</div>
          </div>
          <div className="neo-card p-6 text-center">
            <TrendingUp className="w-6 h-6 mx-auto mb-2 text-[#CCFF00]" />
            <div className="text-3xl font-bold">${stats?.total_earnings?.toFixed(2) || "0.00"}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Total Earned</div>
          </div>
          <div className="neo-card p-6 text-center">
            <Wallet className="w-6 h-6 mx-auto mb-2 text-black" />
            <div className="text-3xl font-bold">${stats?.pending_payout?.toFixed(2) || "0.00"}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Available</div>
          </div>
        </motion.div>

        {/* Create Affiliate Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="neo-card p-6 mb-8"
        >
          <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            Create Affiliate Link
          </h2>
          <form onSubmit={createAffiliateLink} className="flex gap-3">
            <Input
              value={newLinkUrl}
              onChange={(e) => setNewLinkUrl(e.target.value)}
              placeholder="Paste product URL from Etsy, Shopify, etc."
              className="flex-1 border-2 border-black"
              data-testid="affiliate-url-input"
            />
            <Button
              type="submit"
              disabled={creatingLink || !newLinkUrl.trim()}
              data-testid="create-affiliate-link-btn"
              className="bg-black text-white hover:bg-[#CCFF00] hover:text-black border-2 border-black font-bold"
            >
              {creatingLink ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent animate-spin rounded-full" />
              ) : (
                "Generate Link"
              )}
            </Button>
          </form>
          <p className="text-xs text-gray-500 mt-2">
            Paste any product URL and we'll create a trackable affiliate link for you.
          </p>
        </motion.div>

        {/* Request Payout */}
        {stats?.pending_payout >= stats?.minimum_payout && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="neo-card p-6 mb-8 bg-[#CCFF00]/20 border-[#CCFF00]"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">Ready for Payout!</h3>
                <p className="text-sm text-gray-600">
                  You have ${stats?.pending_payout?.toFixed(2)} available for withdrawal.
                </p>
              </div>
              <Button
                onClick={requestPayout}
                disabled={requestingPayout}
                data-testid="request-payout-btn"
                className="bg-black text-white hover:bg-[#CCFF00] hover:text-black border-2 border-black font-bold"
              >
                {requestingPayout ? "Processing..." : "Request Payout"}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Active Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <h2 className="text-sm font-mono uppercase tracking-widest text-gray-500 mb-4">
            YOUR AFFILIATE LINKS ({links.length})
          </h2>

          {links.length > 0 ? (
            <div className="neo-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-black">
                    <TableHead className="font-bold">Link</TableHead>
                    <TableHead className="font-bold text-center">Clicks</TableHead>
                    <TableHead className="font-bold text-center">Sales</TableHead>
                    <TableHead className="font-bold text-right">Earnings</TableHead>
                    <TableHead className="font-bold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {links.map((link) => (
                    <TableRow key={link.link_id} className="border-b border-gray-200">
                      <TableCell className="max-w-[200px]">
                        <div className="truncate text-sm font-mono">
                          {link.original_url}
                        </div>
                        <div className="text-xs text-gray-400 truncate">
                          {link.affiliate_url}
                        </div>
                      </TableCell>
                      <TableCell className="text-center font-bold">
                        {link.clicks}
                      </TableCell>
                      <TableCell className="text-center font-bold">
                        {link.conversions}
                      </TableCell>
                      <TableCell className="text-right font-bold text-green-600">
                        ${link.earnings?.toFixed(2) || "0.00"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyLink(link.affiliate_url)}
                            className="h-8 w-8 p-0"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(link.original_url, "_blank")}
                            className="h-8 w-8 p-0"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="neo-card p-8 text-center">
              <Link2 className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-bold mb-2">No affiliate links yet</h3>
              <p className="text-gray-500 text-sm">
                Create your first affiliate link above and start earning!
              </p>
            </div>
          )}
        </motion.div>

        {/* Payout History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="text-sm font-mono uppercase tracking-widest text-gray-500 mb-4">
            PAYOUT HISTORY
          </h2>

          {payouts.length > 0 ? (
            <div className="space-y-3">
              {payouts.map((payout) => (
                <div
                  key={payout.payout_id}
                  className="neo-card p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    {payout.status === "completed" ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : payout.status === "pending" ? (
                      <Clock className="w-5 h-5 text-yellow-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-blue-500" />
                    )}
                    <div>
                      <div className="font-bold">${payout.amount?.toFixed(2)}</div>
                      <div className="text-xs text-gray-500">
                        {formatDate(payout.created_at)}
                      </div>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 text-xs font-bold uppercase ${
                      payout.status === "completed"
                        ? "bg-green-100 text-green-700"
                        : payout.status === "pending"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {payout.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="neo-card p-8 text-center text-gray-500">
              <p>No payouts yet. Minimum payout is ${stats?.minimum_payout?.toFixed(2)}.</p>
            </div>
          )}
        </motion.div>

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 neo-card p-6"
        >
          <h2 className="font-bold text-lg mb-4">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center p-4">
              <div className="w-10 h-10 bg-[#CCFF00] border-2 border-black flex items-center justify-center mx-auto mb-3 font-bold">
                1
              </div>
              <h3 className="font-bold mb-1">Create Links</h3>
              <p className="text-sm text-gray-500">
                Paste any product URL to generate your trackable affiliate link
              </p>
            </div>
            <div className="text-center p-4">
              <div className="w-10 h-10 bg-[#FF0099] text-white border-2 border-black flex items-center justify-center mx-auto mb-3 font-bold">
                2
              </div>
              <h3 className="font-bold mb-1">Share Content</h3>
              <p className="text-sm text-gray-500">
                Share your links on social media, in posts, or anywhere online
              </p>
            </div>
            <div className="text-center p-4">
              <div className="w-10 h-10 bg-[#7C3AED] text-white border-2 border-black flex items-center justify-center mx-auto mb-3 font-bold">
                3
              </div>
              <h3 className="font-bold mb-1">Earn Commission</h3>
              <p className="text-sm text-gray-500">
                Get {stats?.commission_rate}% of every sale made through your links
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AffiliatePage;
